import api from "../api";

export const expendituresService = {
  // Get expenditures with filters
  getExpenditures: async (filters = {}) => {
    try {
      const response = await api.post(
        "/expenditures/expenditures-items",
        filters
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching expenditures:", error);
      throw error;
    }
  },

  // Get property expenditures
  getPropertyExpenditures: async (propertyId, filters = {}) => {
    try {
      const response = await api.post(
        `/expenditures/properties/${propertyId}/expenditures-items`,
        filters
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching property expenditures:", error);
      throw error;
    }
  },

  // Get expenditure items for a specific property
  getPropertyExpenditureItems: async (propertyId, filters = {}) => {
    try {
      const response = await api.post(
        `/expenditures/properties/${propertyId}/expenditures-items`,
        filters
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching property expenditure items:", error);
      throw error;
    }
  },

  // Get the aggregated expenditure summary for every property of a client
  // filters: { date_from, date_to, include_void }
  getClientExpenditureSummary: async (clientId, filters = {}) => {
    try {
      const response = await api.post(
        `/expenditures/clients/${clientId}/summary`,
        filters
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching client expenditure summary:", error);
      throw error;
    }
  },

  // Get a single expenditure
  getExpenditure: async (id) => {
    try {
      const response = await api.get(`/expenditures/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching expenditure:", error);
      throw error;
    }
  },

  // Get a single expense account item
  getExpenseAccountItem: async (id) => {
    try {
      const response = await api.get(`/finance/expense-account/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching expense account item:", error);
      throw error;
    }
  },

  // Create new expenditure (Laravel route: expenditures.store)
  createExpenditure: async (formData) => {
    try {
      console.log("📡 [FRONTEND] Calling create expenditure API");
      // Laravel route: POST /expenditures/create (expenditures.store)
      const response = await api.post(
        "/expenditures/create",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      console.log(
        "✅ [FRONTEND] Create expenditure API response:",
        response.data
      );
      return response.data;
    } catch (error) {
      console.error("❌ [FRONTEND] Error creating expenditure:", error);
      throw error;
    }
  },

  // Create multiple expenses (for expense account)
  createManyExpenses: async (formData) => {
    try {
      console.log("📡 [FRONTEND] Calling create many expenses API");
      const response = await api.post("/finance/expense-account", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      console.log(
        "✅ [FRONTEND] Create many expenses API response:",
        response.data
      );
      return response.data;
    } catch (error) {
      console.error("❌ [FRONTEND] Error creating many expenses:", error);
      throw error;
    }
  },

  // Create single expense (for expense account)
  createExpenses: async (data) => {
    try {
      console.log("📡 [FRONTEND] Calling create expenses API with data:", data);
      const response = await api.post("/finance/expense-account/store", data);
      console.log("✅ [FRONTEND] Create expenses API response:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ [FRONTEND] Error creating expenses:", error);
      throw error;
    }
  },

  // Update expenditure (Laravel route: expenditures.update)
  // Supports both FormData (with files) and JSON (without files)
  updateExpenditure: async (id, data) => {
    try {
      // Check if data is FormData to set proper headers
      const isFormData = data instanceof FormData;
      const config = isFormData
        ? {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        : {};
      
      const response = await api.put(`/expenditures/${id}`, data, config);
      return response.data;
    } catch (error) {
      console.error("Error updating expenditure:", error);
      throw error;
    }
  },

  // Update expense account item
  updateExpenseAccountItem: async (id, formData) => {
    try {
      const response = await api.put(
        `/finance/expense-account/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating expense account item:", error);
      throw error;
    }
  },

  // Delete expenditure
  deleteExpenditure: async (propertyId, id) => {
    try {
      const response = await api.delete(
        `/properties/${propertyId}/expenditures/items/delete/${id}`
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting expenditure:", error);
      throw error;
    }
  },

  // Toggle void status
  toggleVoid: async (expenditureId) => {
    try {
      const response = await api.post("/expenditures/toggle-void", {
        expenditure_id: expenditureId,
      });
      return response.data;
    } catch (error) {
      console.error("Error toggling void status:", error);
      throw error;
    }
  },

  // Get expenditure categories
  getExpenditureCategories: async () => {
    try {
      const response = await api.get("/expenditures/categories");
      return response.data;
    } catch (error) {
      console.error("Error fetching expenditure categories:", error);
      throw error;
    }
  },

  // Get expenditure types
  getExpenditureTypes: async () => {
    try {
      const response = await api.get("/expenditures/types");
      return response.data;
    } catch (error) {
      console.error("Error fetching expenditure types:", error);
      throw error;
    }
  },

  // Search properties
  searchProperties: async (query) => {
    try {
      console.log(
        "🔍 [SERVICE] Making API call to:",
        `/properties/search?q=${encodeURIComponent(query)}`
      );
      const response = await api.get(
        `/properties/search?q=${encodeURIComponent(query)}`
      );
      console.log("🔍 [SERVICE] API response:", response.data);
      return response.data.properties || [];
    } catch (error) {
      console.error("❌ [SERVICE] Error searching properties:", error);
      throw error;
    }
  },

  // Get bank accounts
  getBankAccounts: async () => {
    try {
      const response = await api.get("/bankaccounts");
      return response.data;
    } catch (error) {
      console.error("Error fetching bank accounts:", error);
      throw error;
    }
  },

  // Update expenditure type
  updateType: async (typeId, data) => {
    try {
      const response = await api.post(
        `/expenditures/types/${typeId}/update`,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error updating expenditure type:", error);
      throw error;
    }
  },

  // Get pay form data
  getPayFormData: async (id) => {
    try {
      const response = await api.get(`/finance/expense-account/${id}/pay`);
      return response.data;
    } catch (error) {
      console.error("Error fetching pay form data:", error);
      throw error;
    }
  },

  // Update payment
  updatePayment: async (id, formData) => {
    try {
      const response = await api.post(
        `/finance/expense-account/${id}/pay`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating payment:", error);
      throw error;
    }
  },

  // Get file
  getFile: async (id, field) => {
    try {
      const response = await api.get(`/expenditures/${id}/${field}`, {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("Error getting file:", error);
      throw error;
    }
  },

  // Remove file (Laravel route: expenditures.file.remove)
  removeFile: async (id, field) => {
    try {
      const response = await api.get(`/expenditures/${id}/remove/${field}`);
      return response.data;
    } catch (error) {
      console.error("Error removing file:", error);
      throw error;
    }
  },

  // Download file from expense account
  // Uses expenditures API route which generates presigned URL
  // Laravel route: /expenditures/{id}/{field}
  downloadFile: async (id, field) => {
    try {
      // Fetch the presigned URL using authenticated API call
      // Backend returns JSON with presigned URL when Authorization header is present
      const response = await api.get(`/expenditures/${id}/${field}`, {
        headers: {
          'X-API-Call': 'true', // Signal this is an API call, not direct browser access
        },
      });

      // Backend returns: { success: true, url: presignedUrl, filename: filename }
      if (response.data && response.data.url) {
        const presignedUrl = response.data.url;
        const filename = response.data.filename || `${field}_${id}.pdf`;
        
        // Fetch the file as a blob from the presigned URL to ensure proper download
        // This avoids browser issues with opening presigned URLs directly
        try {
          const fileResponse = await fetch(presignedUrl, {
            method: 'GET',
            // No CORS headers needed for presigned URLs
          });

          if (!fileResponse.ok) {
            throw new Error(`Failed to fetch file: ${fileResponse.statusText}`);
          }

          // Get the file as a blob
          const blob = await fileResponse.blob();
          
          // Create a blob URL and trigger download
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = filename; // Set download filename
          link.style.display = 'none';
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up blob URL after a short delay
          setTimeout(() => {
            window.URL.revokeObjectURL(blobUrl);
          }, 100);
          
          return { success: true };
        } catch (fetchError) {
          console.error('Error fetching file from presigned URL:', fetchError);
          // Fallback: Try direct link (may not work in all browsers)
          const link = document.createElement('a');
          link.href = presignedUrl;
          link.download = filename;
          link.target = '_blank';
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return { success: true };
        }
      } else {
        throw new Error('No presigned URL received from backend');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  },

  // Remove file
  removeFile: async (id, field) => {
    try {
      const response = await api.get(`/expenditures/${id}/remove/${field}`);
      return response.data;
    } catch (error) {
      console.error("Error removing file:", error);
      throw error;
    }
  },

  // Get properties
  getProperties: async () => {
    try {
      const response = await api.get("/properties");
      return response.data;
    } catch (error) {
      console.error("Error fetching properties:", error);
      throw error;
    }
  },

  // Get expenditure categories
  getCategories: async () => {
    try {
      const response = await api.get("/expenditures/categories");
      return response.data;
    } catch (error) {
      console.error("Error fetching categories:", error);
      throw error;
    }
  },

  // Get expenditure types
  getTypes: async () => {
    try {
      const response = await api.get("/expenditures/types");
      return response.data;
    } catch (error) {
      console.error("Error fetching types:", error);
      throw error;
    }
  },

  // Export expenditures as CSV
  exportCSV: async (filters = {}) => {
    try {
      const response = await api.post("/expenditures/export/csv", filters, {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("Error exporting CSV:", error);
      throw error;
    }
  },

  // Export expenditures as PDF
  exportPDF: async (filters = {}) => {
    try {
      const response = await api.post("/expenditures/export/pdf", filters, {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("Error exporting PDF:", error);
      throw error;
    }
  },

  // Get expenditure statistics
  getStatistics: async (filters = {}) => {
    try {
      const response = await api.post("/expenditures/statistics", filters);
      return response.data;
    } catch (error) {
      console.error("Error fetching statistics:", error);
      throw error;
    }
  },

  // Search expenditures
  searchExpenditures: async (query, filters = {}) => {
    try {
      const response = await api.post("/expenditures/search", {
        query,
        ...filters,
      });
      return response.data;
    } catch (error) {
      console.error("Error searching expenditures:", error);
      throw error;
    }
  },
};

export default expendituresService;
